<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { verifyDemoCode, resendDemoCode } from '../services/demo.service'

const { t } = useI18n()
const authStore = useAuthStore()
const toast = useToastStore()

const digits = ref(['', '', '', '', '', ''])
const inputRefs = ref<(HTMLInputElement | null)[]>([])
const loading = ref(false)
const resendLoading = ref(false)
const showResend = ref(false)
const resendCooldown = ref(0)
let cooldownInterval: ReturnType<typeof setInterval> | null = null

const userEmail = computed(() => authStore.user?.demo_email || authStore.user?.email || '')
const code = computed(() => digits.value.join(''))

const verifyTextParts = computed(() => {
  const raw = t('demo.verify.text', { email: 'EMAIL' })
  const [before, after = ''] = raw.split('EMAIL')
  return { before, after }
})

onMounted(() => {
  nextTick(() => {
    inputRefs.value[0]?.focus()
  })
})

const setInputRef = (el: any, index: number) => {
  inputRefs.value[index] = el
}

const handleInput = (index: number, event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value.replace(/\D/g, '')
  digits.value[index] = value ? value[value.length - 1] : ''

  if (digits.value[index] && index < 5) {
    nextTick(() => inputRefs.value[index + 1]?.focus())
  }
}

const handleKeydown = (index: number, event: KeyboardEvent) => {
  if (event.key === 'Backspace' && !digits.value[index] && index > 0) {
    nextTick(() => inputRefs.value[index - 1]?.focus())
  }
}

const handlePaste = (event: ClipboardEvent) => {
  event.preventDefault()
  const text = event.clipboardData?.getData('text') || ''
  const numbersOnly = text.replace(/\D/g, '').slice(0, 6)
  numbersOnly.split('').forEach((char, i) => {
    digits.value[i] = char
  })
  const nextEmpty = numbersOnly.length < 6 ? numbersOnly.length : 5
  nextTick(() => inputRefs.value[nextEmpty]?.focus())
}

const handleVerify = async () => {
  if (code.value.length !== 6) return

  loading.value = true
  try {
    await verifyDemoCode(code.value)
    toast.success(t('demo.verify.success'))
    authStore.showDemoCredentialsSent = true
    await authStore.finalizeDemoActivation()
  } catch (err: any) {
    const errorCode = err.response?.data?.error_code
    if (errorCode === 'DEMO_CODE_EXPIRED') {
      toast.error(t('demo.verify.errorExpired'))
      showResend.value = true
    } else {
      toast.error(t('demo.verify.errorInvalid'))
      digits.value = ['', '', '', '', '', '']
      nextTick(() => inputRefs.value[0]?.focus())
    }
  } finally {
    loading.value = false
  }
}

const startCooldown = (seconds: number) => {
  resendCooldown.value = seconds
  if (cooldownInterval) clearInterval(cooldownInterval)
  cooldownInterval = setInterval(() => {
    resendCooldown.value -= 1
    if (resendCooldown.value <= 0) {
      clearInterval(cooldownInterval!)
      cooldownInterval = null
    }
  }, 1000)
}

const handleResend = async () => {
  if (resendCooldown.value > 0 || resendLoading.value) return

  resendLoading.value = true
  try {
    await resendDemoCode()
    toast.success(t('demo.verify.resendSuccess'))
    startCooldown(60)
    digits.value = ['', '', '', '', '', '']
    nextTick(() => inputRefs.value[0]?.focus())
  } catch (err: any) {
    const secondsRemaining = err.response?.data?.seconds_remaining || 60
    toast.error(t('demo.verify.resendThrottle'))
    startCooldown(secondsRemaining)
  } finally {
    resendLoading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style="background: rgba(15, 23, 42, 0.35); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);"
    >
      <div class="bg-white rounded-lg shadow-xl w-full max-w-md">
        <!-- Header -->
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">{{ t('demo.verify.title') }}</h2>
        </div>

        <!-- Body -->
        <div class="p-6 space-y-5">
          <p class="text-sm text-gray-600">
            {{ verifyTextParts.before }}<strong class="text-gray-900">{{ userEmail }}</strong>{{ verifyTextParts.after }}
          </p>

          <!-- 6-digit code inputs -->
          <div class="flex justify-center gap-2" @paste="handlePaste">
            <input
              v-for="(_, index) in digits"
              :key="index"
              :ref="(el) => setInputRef(el, index)"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="1"
              :value="digits[index]"
              @input="handleInput(index, $event)"
              @keydown="handleKeydown(index, $event)"
              :disabled="loading"
              class="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 disabled:opacity-50 caret-transparent"
            />
          </div>

          <!-- Activate button -->
          <button
            @click="handleVerify"
            :disabled="code.length !== 6 || loading"
            class="w-full py-2.5 px-4 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <svg v-if="loading" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            {{ t('demo.verify.activate') }}
          </button>

          <!-- Resend section -->
          <div class="text-center">
            <button
              @click="handleResend"
              :disabled="resendCooldown > 0 || resendLoading"
              class="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <span v-if="resendCooldown > 0">{{ t('demo.verify.resendIn', { seconds: resendCooldown }) }}</span>
              <span v-else>{{ t('demo.verify.resend') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
